import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { useAuth } from "../../hooks/useAuth";

// ═══════════════════════════════════════════════════════════════════════════
// RBACContext — Enterprise Role-Based Access Control
// Canonical roles: OWNER, PHARMACIST, TECH, AE, LIAISON, SCHEDULER
// A user may hold MULTIPLE roles (e.g., TECH + SCHEDULER).
// The "activeRole" determines the current dashboard view.
// "simulatedRole" lets Owners preview what other roles see.
// ═══════════════════════════════════════════════════════════════════════════

export const ROLES = {
  OWNER:      { key: "owner",      label: "Owner",              color: "#17618a", bg: "#eef5f9", icon: "👑" },
  PHARMACIST: { key: "pharmacist", label: "Pharmacist",         color: "#059669", bg: "#ecfdf5", icon: "💊" },
  INTAKE:     { key: "intake",     label: "Intake Coordinator", color: "#0891b2", bg: "#ecfeff", icon: "📥" },
  TECH:       { key: "technician", label: "Technician",         color: "#ea580c", bg: "#fff7ed", icon: "🔧" },
  AE:         { key: "ae",         label: "Account Exec",       color: "#d97706", bg: "#fffbeb", icon: "📈" },
  LIAISON:    { key: "liaison",    label: "Liaison",            color: "#dc2626", bg: "#fef2f2", icon: "🏥" },
  SCHEDULER:  { key: "scheduler",  label: "Scheduler",          color: "#7c3aed", bg: "#f5f3ff", icon: "📅" },
};

// Map Cognito role strings → canonical keys. Intake is a distinct workflow
// role (referral triage before Technician dose calc) and is NOT an alias
// for technician — they must not share permissions or queues.
const LEGACY_MAP = {
  owner: "owner", pharmacist: "pharmacist", technician: "technician",
  tech: "technician", intake: "intake", ae: "ae",
  liaison: "liaison", scheduler: "scheduler",
};

// Financial fields that TECH/AE/LIAISON/SCHEDULER must never see
export const FINANCIAL_FIELDS = [
  "costPerVial", "costPerGram", "drugCostPerVial", "drugCostPerGram",
  "wacPrice", "wholesaleCost", "marginPercent", "marginDollars",
  "spread", "reimbursement", "profitPerUnit", "totalProfit",
  "estimatedRevenue", "estimatedCost", "estimatedMargin",
];

const RBACContext = createContext(null);

export function RBACProvider({ children }) {
  const auth = useAuth();
  // auth.cognitoRole is the REAL Cognito role (auth.role may be the simulated
  // "View As" value — don't use that for RBAC authority decisions like
  // canSimulate / isOwner).
  const rawRole = (auth.cognitoRole || auth.role || "").toLowerCase();
  const cognitoRole = LEGACY_MAP[rawRole] || rawRole || "technician";

  // Roles this user is assigned to (stored in localStorage for dev-testing,
  // production reads from Cognito custom attributes or team-management API)
  const [assignedRoles, setAssignedRoles] = useState(() => {
    try {
      const saved = localStorage.getItem("vc_assigned_roles");
      return saved ? JSON.parse(saved) : [cognitoRole];
    } catch { return [cognitoRole]; }
  });

  // Active role = the one currently shown in the dashboard
  const [activeRole, setActiveRole] = useState(() => {
    return localStorage.getItem("vc_active_role") || cognitoRole;
  });

  // ── "View As" simulation — only available to Owners ─────────────────
  // Persisted to localStorage + dispatched as a window event so every
  // useAuth()/usePermission() consumer across the app re-renders with
  // the simulated role, closing the root leak where files that read
  // useAuth().role directly bypassed the simulation.
  const [simulatedRole, setSimulatedRoleState] = useState(() => {
    try { return localStorage.getItem("vc_simulated_role") || null; } catch { return null; }
  });
  const setSimulatedRole = useCallback((r) => {
    setSimulatedRoleState(r);
    try {
      if (r) localStorage.setItem("vc_simulated_role", r);
      else localStorage.removeItem("vc_simulated_role");
      if (typeof window !== "undefined") {
        window.dispatchEvent(new Event("vc_simulated_role_change"));
      }
    } catch {}
  }, []);
  const effectiveRole = simulatedRole || activeRole;

  // Persist to localStorage for dev-testing switcher
  useEffect(() => {
    localStorage.setItem("vc_assigned_roles", JSON.stringify(assignedRoles));
  }, [assignedRoles]);

  useEffect(() => {
    localStorage.setItem("vc_active_role", activeRole);
  }, [activeRole]);

  // Override from Cognito on first load (production path)
  useEffect(() => {
    if (!localStorage.getItem("vc_assigned_roles")) {
      setAssignedRoles([cognitoRole]);
      setActiveRole(cognitoRole);
    }
  }, [cognitoRole]);

  const switchRole = useCallback((role) => {
    if (assignedRoles.includes(role) || cognitoRole === "owner") {
      setActiveRole(role);
    }
  }, [assignedRoles, cognitoRole]);

  // Can the EFFECTIVE role see financial data?
  const canSeeFinancials = ["owner", "pharmacist"].includes(effectiveRole);

  // Strip financial fields from a patient object for non-financial roles
  const maskFinancials = useCallback((patient) => {
    if (canSeeFinancials) return patient;
    const masked = { ...patient };
    FINANCIAL_FIELDS.forEach(f => { delete masked[f]; });
    if (masked.referralAnalysis?.drugMatch?.catalogDrug) {
      masked.referralAnalysis = {
        ...masked.referralAnalysis,
        drugMatch: {
          ...masked.referralAnalysis.drugMatch,
          catalogDrug: { ...masked.referralAnalysis.drugMatch.catalogDrug, costPerVial: undefined, costPerGram: undefined },
        },
      };
    }
    return masked;
  }, [canSeeFinancials]);

  const value = {
    // Auth pass-through
    ...auth,
    // RBAC state
    cognitoRole,
    assignedRoles,
    activeRole,
    effectiveRole,
    switchRole,
    setAssignedRoles,
    // "View As" simulation
    simulatedRole,
    setSimulatedRole,
    canSimulate: cognitoRole === "owner",
    // Helpers
    canSeeFinancials,
    maskFinancials,
    isOwner: cognitoRole === "owner",
    isPharmacist: ["owner", "pharmacist"].includes(effectiveRole),
    isIntake: effectiveRole === "intake",
    isTech: effectiveRole === "technician",
    isAE: effectiveRole === "ae",
    isLiaison: effectiveRole === "liaison",
    isScheduler: effectiveRole === "scheduler",
  };

  return <RBACContext.Provider value={value}>{children}</RBACContext.Provider>;
}

export function useRBAC() {
  const ctx = useContext(RBACContext);
  if (!ctx) throw new Error("useRBAC must be used inside <RBACProvider>");
  return ctx;
}
